import type { Agency, Employee, ShiftAssignment } from "./types";
import { effectiveHomeShiftFor, isTacActiveOn } from "./tacMode";

// Resolve which shift an officer is *actually* working on a given
// date, accounting for any temporary shift_assignments rows that
// override their homeShiftId. The materialiser picks the override
// row with the latest startDate when multiple cover the same day,
// so a more-specific later assignment can shadow an earlier one.
//
// Build an EffectiveShiftLookup once per query window and reuse it
// across every cell render — cheaper than scanning the
// shift_assignments list per cell.
export interface EffectiveShiftLookup {
  // Returns the effective shiftId for (employeeId, date), or null
  // if the officer is unassigned that day.
  get(employeeId: string, date: string): string | null;
  // Every shiftId that ANY override row in the window targets for
  // this employee. Used by the grid grouping so an officer who
  // alternates between two shifts in the visible window shows up
  // under both shift sections.
  shiftsFor(employeeId: string): Set<string>;
}

export function buildEffectiveShiftLookup(
  employees: Employee[],
  shiftAssignments: ShiftAssignment[],
  // Optional tac context. When provided, the lookup applies the
  // tac overlay per-date so a window straddling the tac start date
  // groups the officer under their regular home shift before the
  // date and their tac home shift on/after. Omit (null) for legacy
  // callers that only want the raw shift_assignments overlay.
  agency: Agency | null | undefined = null,
  windowDates: string[] = [],
): EffectiveShiftLookup {
  // Per-employee, override rows sorted by startDate descending so
  // the first row whose range contains the target date wins.
  const byEmp = new Map<string, ShiftAssignment[]>();
  for (const sa of shiftAssignments) {
    const list = byEmp.get(sa.employeeId) ?? [];
    list.push(sa);
    byEmp.set(sa.employeeId, list);
  }
  for (const list of byEmp.values()) {
    list.sort((a, b) => b.startDate.localeCompare(a.startDate));
  }
  const empById = new Map(employees.map((e) => [e.id, e]));

  return {
    get(employeeId: string, date: string): string | null {
      const overrides = byEmp.get(employeeId);
      if (overrides) {
        for (const o of overrides) {
          if (date >= o.startDate && date <= o.endDate) return o.shiftId;
        }
      }
      const emp = empById.get(employeeId);
      if (!emp) return null;
      return effectiveHomeShiftFor(agency, emp, date);
    },
    shiftsFor(employeeId: string): Set<string> {
      const out = new Set<string>();
      const emp = empById.get(employeeId);
      if (emp) {
        // Both possible home assignments — the raw homeShiftId
        // for regular-mode dates and the tacHomeShiftId for
        // dates where tac is active in the window. For a window
        // entirely outside the tac period only the raw home is
        // added; for one entirely inside only the tac home (if
        // the officer follows tac mode) is added; for one that
        // straddles, both are added so the officer renders under
        // both sections and the per-cell logic gates the days.
        if (windowDates.length === 0) {
          if (emp.homeShiftId) out.add(emp.homeShiftId);
        } else {
          let sawRegular = false;
          let sawTac = false;
          for (const d of windowDates) {
            if (isTacActiveOn(agency, d)) sawTac = true;
            else sawRegular = true;
            if (sawRegular && sawTac) break;
          }
          if (sawRegular && emp.homeShiftId) out.add(emp.homeShiftId);
          if (sawTac && emp.followsTacMode && emp.tacHomeShiftId) {
            out.add(emp.tacHomeShiftId);
          }
          if (sawTac && !emp.followsTacMode && emp.homeShiftId) {
            // Officer doesn't follow tac → keeps regular home on
            // tac days too. Add it so they still show up.
            out.add(emp.homeShiftId);
          }
        }
      }
      const overrides = byEmp.get(employeeId);
      if (overrides) for (const o of overrides) out.add(o.shiftId);
      return out;
    },
  };
}
